import { Link } from "react-router-dom";
import { LegalLayout } from "@/components/legal/LegalLayout";
import { BUSINESS } from "@/lib/legal";

const sections = [
  {
    id: "objeto",
    title: "Objeto",
    content: (
      <p>
        Las presentes Condiciones regulan la contratación online de los servicios de lavandería ofrecidos por{" "}
        <span className="notranslate">{BUSINESS.name}</span> (NIF <span className="notranslate">{BUSINESS.nif}</span>, {BUSINESS.address})
        a través de este sitio web y de su aplicación: lavado, secado, planchado, ropa de cama y edredones, con recogida y entrega a
        domicilio. Al marcar la casilla de aceptación antes del pago, el cliente declara haber leído y aceptado estas Condiciones y la{" "}
        <Link to="/privacidad" className="text-cyan-700 underline underline-offset-2">Política de privacidad</Link>.
      </p>
    ),
  },
  {
    id: "precios",
    title: "Precios y pago",
    content: (
      <>
        <p>
          Los precios de los servicios online se muestran en euros en la página de reserva y se confirman en el resumen antes del pago.
          Los impuestos aplicables se calculan y detallan en la pasarela de pago. Los precios del autoservicio en tienda (lavadoras,
          secadoras, tarjeta de cliente, vending) se abonan directamente en el local y son independientes de la reserva online.
        </p>
        <p>
          El pago se realiza por adelantado mediante tarjeta a través de Stripe, un proveedor certificado PCI-DSS. <span className="notranslate">{BUSINESS.name}</span>{" "}
          no almacena los datos de la tarjeta. El pedido queda confirmado cuando el pago se completa con éxito; en ese momento el cliente
          recibe un correo electrónico con el número de pedido.
        </p>
      </>
    ),
  },
  {
    id: "desplazamiento",
    title: "Coste de desplazamiento (gasolina)",
    content: (
      <>
        <p>
          A los pedidos con recogida a domicilio se les aplica un coste de desplazamiento calculado automáticamente en función de la
          distancia por carretera entre nuestro local ({BUSINESS.address}) y la dirección de recogida indicada por el cliente:
        </p>
        <ul className="list-disc pl-5 space-y-1">
          <li><strong>Gratis</strong> si la dirección está a <strong>3 km o menos</strong> del local.</li>
          <li>A partir de 3 km, <strong>0,50 € por cada kilómetro adicional</strong> (por ejemplo, 5 km → 2 km extra → 1,00 €).</li>
          <li>Zona máxima de reparto: 40 km. Las direcciones fuera de esta zona no pueden reservarse online.</li>
        </ul>
        <p>
          El importe exacto se muestra en el resumen del pedido antes de pagar y figura como línea «Desplazamiento» en el pago y en el
          correo de confirmación. La distancia se obtiene de servicios cartográficos de terceros (OpenStreetMap/OSRM) y puede presentar
          pequeñas variaciones respecto al trayecto real; el importe mostrado en el momento del pago es el definitivo. El servicio
          «Recogida a Domicilio» del catálogo es independiente y adicional a este coste de desplazamiento.
        </p>
      </>
    ),
  },
  {
    id: "recogida",
    title: "Recogida y entrega",
    content: (
      <>
        <p>
          La recogida se realiza en la fecha elegida por el cliente, dentro de nuestro horario ({BUSINESS.hours}). Nos pondremos en
          contacto por teléfono o WhatsApp para concretar la franja horaria. El cliente debe asegurarse de que haya alguien en la
          dirección indicada o de dejar instrucciones en el campo de notas.
        </p>
        <p>
          El plazo habitual de entrega es de 24 a 48 horas desde la recogida, salvo prendas especiales (edredones, planchado voluminoso)
          o circunstancias excepcionales, que se comunicarán al cliente. El estado del pedido puede consultarse en la sección{" "}
          <Link to="/seguimiento" className="text-cyan-700 underline underline-offset-2">Seguimiento</Link> o en la cuenta de cliente.
        </p>
      </>
    ),
  },
  {
    id: "cancelacion",
    title: "Modificaciones y cancelaciones",
    content: (
      <p>
        El cliente puede modificar o cancelar su pedido sin coste hasta el momento de la recogida, escribiendo a {BUSINESS.email} o por
        WhatsApp al {BUSINESS.phone}, indicando el número de pedido. En caso de cancelación antes de la recogida se devolverá el importe
        íntegro por el mismo medio de pago en un plazo máximo de 14 días. Una vez recogida la ropa, el servicio se considera iniciado y no
        será posible la cancelación, aunque sí podrá ajustarse el pedido si varía la cantidad de prendas o cargas.
      </p>
    ),
  },
  {
    id: "desistimiento",
    title: "Derecho de desistimiento",
    content: (
      <p>
        De acuerdo con el artículo 103.a) del Real Decreto Legislativo 1/2007 (Ley General para la Defensa de los Consumidores y
        Usuarios), el derecho de desistimiento no será aplicable a los servicios que hayan sido completamente ejecutados cuando la
        ejecución haya comenzado con el consentimiento expreso del consumidor. Al aceptar estas Condiciones y solicitar una fecha de
        recogida, el cliente consiente que el servicio comience antes de que finalice el plazo de desistimiento de 14 días. Hasta la
        recogida, el cliente puede cancelar según lo indicado en el apartado anterior.
      </p>
    ),
  },
  {
    id: "prendas",
    title: "Cuidado de las prendas y responsabilidad",
    content: (
      <>
        <p>
          Tratamos cada prenda siguiendo las indicaciones de su etiqueta. El cliente debe revisar los bolsillos y advertir en las notas de
          cualquier prenda delicada, mancha especial o instrucción particular. No nos hacemos responsables de:
        </p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Objetos olvidados en bolsillos ni de los daños que estos puedan causar.</li>
          <li>Prendas sin etiqueta de composición o con etiquetas ilegibles, ni de daños derivados de defectos previos del tejido.</li>
          <li>Decoloraciones o encogimientos propios de tejidos que no admiten lavado en agua pese a indicarlo su etiqueta.</li>
        </ul>
        <p>
          En caso de pérdida o deterioro imputable a nuestro servicio, la indemnización se limitará al valor acreditado de la prenda, con
          un máximo de diez veces el precio del servicio contratado para dicha prenda, salvo que el cliente haya declarado y justificado
          un valor superior antes de la recogida. Las reclamaciones deben comunicarse en las 48 horas siguientes a la entrega.
        </p>
      </>
    ),
  },
  {
    id: "cuenta",
    title: "Cuenta de cliente y Tarjeta Cliente",
    content: (
      <p>
        La cuenta de cliente online (acceso con Google) es gratuita y permite guardar datos, consultar el historial y seguir los pedidos.
        El cliente es responsable de la custodia de su cuenta de Google. La Tarjeta Cliente física del local, con precios reducidos en
        el autoservicio, se rige por las condiciones indicadas en el establecimiento y no está vinculada a la cuenta online.
      </p>
    ),
  },
  {
    id: "reclamaciones",
    title: "Atención al cliente y reclamaciones",
    content: (
      <p>
        Para cualquier consulta, incidencia o reclamación puede contactar con nosotros en {BUSINESS.email}, en el {BUSINESS.phone}
        (también WhatsApp) o en el propio local. Disponemos de hojas de reclamaciones oficiales. Puede acudir igualmente a la plataforma
        europea de resolución de litigios en línea (<a href="https://ec.europa.eu/consumers/odr" target="_blank" rel="noopener noreferrer" className="text-cyan-700 underline underline-offset-2">ec.europa.eu/consumers/odr</a>).
      </p>
    ),
  },
  {
    id: "ley",
    title: "Legislación aplicable",
    content: (
      <p>
        Estas Condiciones se rigen por la legislación española, en especial por la Ley 34/2002 (LSSI-CE), el Real Decreto Legislativo
        1/2007 (LGDCU) y la Ley 7/1998 sobre Condiciones Generales de la Contratación. Para cualquier controversia, el consumidor podrá
        acudir a los Juzgados y Tribunales de su domicilio o a los de Zaragoza.
      </p>
    ),
  },
];

export default function Condiciones() {
  return (
    <LegalLayout
      testId="legal-condiciones-page"
      title="Condiciones del servicio"
      subtitle="Términos que aplican a las reservas online con recogida a domicilio: precios, desplazamiento, plazos, cancelaciones y garantías."
      sections={sections}
    />
  );
}
